import { ApiNotificationChannelType } from "@/types/notification.types.ts";
import { ReminderMode, ReminderPeriod, ReminderSettings } from "@/types/reminder.types.ts";
import { TagVariants } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

const reminderModeTagVariants: Record<ReminderMode, TagVariants> = {
  [ReminderMode.light]: "positive",
  [ReminderMode.medium]: "blue",
  [ReminderMode.hard]: "warning",
  [ReminderMode.custom]: "primary",
};

const feminineReminderPeriods = [ReminderPeriod.week];

export function useReminderHook() {
  const { t } = useTranslation();

  const getReminderModeTitle = (mode: ReminderMode): string => {
    switch (mode) {
      case ReminderMode.light:
        return t("core:reminder_mode_gentle");
      case ReminderMode.medium:
        return t("core:reminder_mode_balanced");
      case ReminderMode.hard:
        return t("core:reminder_mode_persistent");
      case ReminderMode.custom:
        return t("core:reminder_mode_custom");
      default:
        return "";
    }
  };

  const getReminderModeDescription = (mode: ReminderMode): string => {
    switch (mode) {
      case ReminderMode.light:
        return t("core:reminder_mode_gentle_description");
      case ReminderMode.medium:
        return t("core:reminder_mode_balanced_description");
      case ReminderMode.hard:
        return t("core:reminder_mode_persistent_description");
      case ReminderMode.custom:
        return t("core:reminder_mode_custom_description");
      default:
        return "";
    }
  };

  const getReminderPeriodTitle = (
    period: ReminderPeriod = ReminderPeriod.day,
    frequency: number = 1
  ): string => {
    const periodTitles: Record<ReminderPeriod, string> = {
      [ReminderPeriod.day]: t("core:reminder_period_day", { count: frequency }),
      [ReminderPeriod.week]: t("core:reminder_period_week", { count: frequency }),
      [ReminderPeriod.month]: t("core:reminder_period_month", { count: frequency }),
    };

    return periodTitles[period];
  };

  const getReminderEveryTitle = (
    period: ReminderPeriod = ReminderPeriod.day,
    frequency: number = 1
  ): string => {
    const remindEveryKey = feminineReminderPeriods.includes(period)
      ? "core:remind_every_feminine"
      : "core:remind_every_masculine";

    return t(remindEveryKey, { count: frequency });
  };

  const getCustomReminderIntervalTitle = (settings: ReminderSettings): string => {
    const frequency = settings.custom_frequency ?? 1;
    const period = settings.custom_period ?? ReminderPeriod.day;
    const frequencyTitle = frequency > 1 ? ` ${frequency}` : "";

    return `${getReminderEveryTitle(period, frequency)}${frequencyTitle} ${getReminderPeriodTitle(period, frequency)}`;
  };

  const getReminderSettingsDescription = (settings: ReminderSettings): string => {
    if (settings.mode !== ReminderMode.custom) {
      return getReminderModeDescription(settings.mode);
    }

    return getCustomReminderIntervalTitle(settings);
  };

  const calculateReminderCount = (daysUntilDeadline: number, mode: ReminderMode): number => {
    if (daysUntilDeadline <= 0) return 0;

    switch (mode) {
      case ReminderMode.light:
        return daysUntilDeadline <= 10 ? 1 : 2;
      case ReminderMode.medium:
        return daysUntilDeadline <= 10 ? 2 : 3;
      case ReminderMode.hard:
        if (daysUntilDeadline <= 2) return 2;
        if (daysUntilDeadline <= 5) return 4;
        return 6;
      default:
        return 0;
    }
  };

  const getReminderModeTagVariant = (mode?: ReminderMode | null): TagVariants => {
    if (!mode) return "secondary";
    return reminderModeTagVariants[mode] ?? "secondary";
  };

  const getNotificationChannelTitle = (type: ApiNotificationChannelType): string => {
    switch (type) {
      case ApiNotificationChannelType.email:
        return "Email";
      case ApiNotificationChannelType.slack:
        return "Slack";
      case ApiNotificationChannelType.messenger:
        return t("core:messenger");
      case ApiNotificationChannelType.in_app:
        return t("core:in_app");
      case ApiNotificationChannelType.slack_channel:
        return t("blog:slack_channel");
      default:
        return "";
    }
  };

  return {
    getReminderModeTitle,
    getReminderModeDescription,
    getReminderSettingsDescription,
    getReminderPeriodTitle,
    getReminderEveryTitle,
    calculateReminderCount,
    getReminderModeTagVariant,
    getNotificationChannelTitle,
  };
}
